/**
 * Interfaz para definir los colores de un tema
 */
export interface ThemeColors {
  // Backgrounds
  bgPrimary: string;
  bgSecondary: string;
  bgTertiary: string;
  bgOverlay: string;
  bgOverlaySecondary: string;

  // Fondo de página (gradientes)
  bgPageFrom: string;
  bgPageVia: string;
  bgPageTo: string;

  // Botones
  btnPrimary: string;
  btnPrimaryHover: string;
  btnPrimaryActive: string;
  btnPrimaryGradientFrom: string;
  btnPrimaryGradientVia: string;
  btnPrimaryGradientTo: string;
  btnPrimaryGradientHoverFrom: string;
  btnPrimaryGradientHoverVia: string;
  btnPrimaryGradientHoverTo: string;

  // Bordes
  borderPrimary: string;
  borderSecondary: string;

  // Textos generales
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textMuted: string;
  textAccent: string;
  textPlaceholder: string;

  // Títulos
  textTitle: string;
  textTitleAccent: string;

  // Subtítulos
  textSubtitle: string;

  // Labels
  textLabel: string;

  // Sombras
  shadowPrimary: string;
  shadowPrimaryHover: string;
  shadowPrimaryLight: string;
  shadowCard: string;
  shadowCardHover: string;
  shadowButton: string;

  // Estados y alertas
  colorFocusRing: string;
  colorFocusBorder: string;
  colorAccentOverlay: string;
  colorAccentRing: string;
  colorErrorBg: string;
  colorErrorBorder: string;
  colorErrorText: string;
  colorWarningBg: string;
  colorWarningBorder: string;
  colorWarningText: string;
  colorInfoBg: string;
  colorInfoBorder: string;
  colorInfoText: string;
  colorSuccessBg: string;
  colorSuccessBorder: string;
  colorSuccessText: string;
  colorOperationalExpense: string;
  colorOperationalExpenseBg: string;
  colorOperationalExpenseBorder: string;
  colorNonOperationalExpense: string;
  colorNonOperationalExpenseBg: string;
  colorNonOperationalExpenseBorder: string;
  colorCheckbox: string;
  colorToggleInactive: string;
  colorToggleInactiveHover: string;

  // Color para iconos SVG en headers (sidebar)
  sidebarIconColor?: string;

  // Constelaciones (fondo animado)
  constellationDot?: string;
  constellationLine?: string;
  constellationDotOpacity?: string;
  constellationLineOpacity?: string;
}

/**
 * Lista única de variables CSS que aplican temas.
 * Usada en resetTheme() para mantener una sola fuente de verdad.
 * Si añades una variable temática: 1) añádela aquí, 2) en ThemeColors, 3) en applyTheme().
 */
export const THEME_CSS_VARS = [
  "--bg-primary",
  "--bg-secondary",
  "--bg-tertiary",
  "--bg-overlay",
  "--bg-overlay-secondary",
  "--bg-page-from",
  "--bg-page-via",
  "--bg-page-to",
  "--btn-primary",
  "--btn-primary-hover",
  "--btn-primary-active",
  "--btn-primary-gradient-from",
  "--btn-primary-gradient-via",
  "--btn-primary-gradient-to",
  "--btn-primary-gradient-hover-from",
  "--btn-primary-gradient-hover-via",
  "--btn-primary-gradient-hover-to",
  "--border-primary",
  "--border-secondary",
  "--text-primary",
  "--text-secondary",
  "--text-tertiary",
  "--text-muted",
  "--text-accent",
  "--text-placeholder",
  "--text-title",
  "--text-title-accent",
  "--text-subtitle",
  "--text-label",
  "--shadow-primary",
  "--shadow-primary-hover",
  "--shadow-primary-light",
  "--shadow-card",
  "--shadow-card-hover",
  "--shadow-button",
  "--color-focus-ring",
  "--color-focus-border",
  "--color-accent-overlay",
  "--color-accent-ring",
  "--color-error-bg",
  "--color-error-border",
  "--color-error-text",
  "--color-warning-bg",
  "--color-warning-border",
  "--color-warning-text",
  "--color-info-bg",
  "--color-info-border",
  "--color-info-text",
  "--color-success-bg",
  "--color-success-border",
  "--color-success-text",
  "--color-operational-expense",
  "--color-operational-expense-bg",
  "--color-operational-expense-border",
  "--color-non-operational-expense",
  "--color-non-operational-expense-bg",
  "--color-non-operational-expense-border",
  "--color-checkbox",
  "--color-toggle-inactive",
  "--color-toggle-inactive-hover",
  "--sidebar-icon-color",
  "--constellation-dot",
  "--constellation-line",
  "--constellation-dot-opacity",
  "--constellation-line-opacity",
] as const;

/**
 * Aplica un tema actualizando todas las variables CSS en el documento
 */
export const applyTheme = (colors: ThemeColors): void => {
  const root = document.documentElement;

  // Backgrounds
  root.style.setProperty("--bg-primary", colors.bgPrimary);
  root.style.setProperty("--bg-secondary", colors.bgSecondary);
  root.style.setProperty("--bg-tertiary", colors.bgTertiary);
  root.style.setProperty("--bg-overlay", colors.bgOverlay);
  root.style.setProperty("--bg-overlay-secondary", colors.bgOverlaySecondary);

  // Fondo de página
  root.style.setProperty("--bg-page-from", colors.bgPageFrom);
  root.style.setProperty("--bg-page-via", colors.bgPageVia);
  root.style.setProperty("--bg-page-to", colors.bgPageTo);

  // Botones
  root.style.setProperty("--btn-primary", colors.btnPrimary);
  root.style.setProperty("--btn-primary-hover", colors.btnPrimaryHover);
  root.style.setProperty("--btn-primary-active", colors.btnPrimaryActive);
  root.style.setProperty(
    "--btn-primary-gradient-from",
    colors.btnPrimaryGradientFrom,
  );
  root.style.setProperty(
    "--btn-primary-gradient-via",
    colors.btnPrimaryGradientVia,
  );
  root.style.setProperty(
    "--btn-primary-gradient-to",
    colors.btnPrimaryGradientTo,
  );
  root.style.setProperty(
    "--btn-primary-gradient-hover-from",
    colors.btnPrimaryGradientHoverFrom,
  );
  root.style.setProperty(
    "--btn-primary-gradient-hover-via",
    colors.btnPrimaryGradientHoverVia,
  );
  root.style.setProperty(
    "--btn-primary-gradient-hover-to",
    colors.btnPrimaryGradientHoverTo,
  );

  // Bordes
  root.style.setProperty("--border-primary", colors.borderPrimary);
  root.style.setProperty("--border-secondary", colors.borderSecondary);

  // Textos generales
  root.style.setProperty("--text-primary", colors.textPrimary);
  root.style.setProperty("--text-secondary", colors.textSecondary);
  root.style.setProperty("--text-tertiary", colors.textTertiary);
  root.style.setProperty("--text-muted", colors.textMuted);
  root.style.setProperty("--text-accent", colors.textAccent);
  root.style.setProperty("--text-placeholder", colors.textPlaceholder);

  // Títulos
  root.style.setProperty("--text-title", colors.textTitle);
  root.style.setProperty("--text-title-accent", colors.textTitleAccent);

  // Subtítulos
  root.style.setProperty("--text-subtitle", colors.textSubtitle);

  // Labels
  root.style.setProperty("--text-label", colors.textLabel);

  // Sombras
  root.style.setProperty("--shadow-primary", colors.shadowPrimary);
  root.style.setProperty("--shadow-primary-hover", colors.shadowPrimaryHover);
  root.style.setProperty("--shadow-primary-light", colors.shadowPrimaryLight);
  root.style.setProperty("--shadow-card", colors.shadowCard);
  root.style.setProperty("--shadow-card-hover", colors.shadowCardHover);
  root.style.setProperty("--shadow-button", colors.shadowButton);

  // Estados y alertas
  root.style.setProperty("--color-focus-ring", colors.colorFocusRing);
  root.style.setProperty("--color-focus-border", colors.colorFocusBorder);
  root.style.setProperty("--color-accent-overlay", colors.colorAccentOverlay);
  root.style.setProperty("--color-accent-ring", colors.colorAccentRing);
  root.style.setProperty("--color-error-bg", colors.colorErrorBg);
  root.style.setProperty("--color-error-border", colors.colorErrorBorder);
  root.style.setProperty("--color-error-text", colors.colorErrorText);
  root.style.setProperty("--color-warning-bg", colors.colorWarningBg);
  root.style.setProperty("--color-warning-border", colors.colorWarningBorder);
  root.style.setProperty("--color-warning-text", colors.colorWarningText);
  root.style.setProperty("--color-info-bg", colors.colorInfoBg);
  root.style.setProperty("--color-info-border", colors.colorInfoBorder);
  root.style.setProperty("--color-info-text", colors.colorInfoText);
  root.style.setProperty("--color-success-bg", colors.colorSuccessBg);
  root.style.setProperty("--color-success-border", colors.colorSuccessBorder);
  root.style.setProperty("--color-success-text", colors.colorSuccessText);
  root.style.setProperty(
    "--color-operational-expense",
    colors.colorOperationalExpense,
  );
  root.style.setProperty(
    "--color-operational-expense-bg",
    colors.colorOperationalExpenseBg,
  );
  root.style.setProperty(
    "--color-operational-expense-border",
    colors.colorOperationalExpenseBorder,
  );
  root.style.setProperty(
    "--color-non-operational-expense",
    colors.colorNonOperationalExpense,
  );
  root.style.setProperty(
    "--color-non-operational-expense-bg",
    colors.colorNonOperationalExpenseBg,
  );
  root.style.setProperty(
    "--color-non-operational-expense-border",
    colors.colorNonOperationalExpenseBorder,
  );
  root.style.setProperty("--color-checkbox", colors.colorCheckbox);
  root.style.setProperty("--color-toggle-inactive", colors.colorToggleInactive);
  root.style.setProperty(
    "--color-toggle-inactive-hover",
    colors.colorToggleInactiveHover,
  );

  // Color para iconos SVG en headers (sidebar)
  if (colors.sidebarIconColor !== undefined) {
    root.style.setProperty("--sidebar-icon-color", colors.sidebarIconColor);
  }

  // Constelaciones
  if (colors.constellationDot !== undefined) {
    root.style.setProperty("--constellation-dot", colors.constellationDot);
  }
  if (colors.constellationLine !== undefined) {
    root.style.setProperty("--constellation-line", colors.constellationLine);
  }
  if (colors.constellationDotOpacity !== undefined) {
    root.style.setProperty(
      "--constellation-dot-opacity",
      colors.constellationDotOpacity,
    );
  }
  if (colors.constellationLineOpacity !== undefined) {
    root.style.setProperty(
      "--constellation-line-opacity",
      colors.constellationLineOpacity,
    );
  }
};

/**
 * Resetea el tema a los valores por defecto definidos en CSS (:root en variables.css)
 */
export const resetTheme = (): void => {
  const root = document.documentElement;
  THEME_CSS_VARS.forEach((variable) => {
    root.style.removeProperty(variable);
  });
};
